import { ModalName } from '@/constants/modals';
import {
	AddWorkerTabId,
	ALL_PROFILES_MIN_SEARCH_LENGTH,
	ALL_PROFILES_SEARCH_LIMIT,
} from '@/modules/companyNeed/components/modals/mission/addWorkerToMission/types';
import companyNeedService from '@/modules/companyNeed/services/companyNeed';
import {
	MISSION_MAX_WORKERS_REACHED_CODE,
	MISSION_WORKER_ALREADY_ASSIGNED_CODE,
	MISSION_WORKER_REGISTRATION_NOT_COMPLETE_CODE,
} from '@/modules/companyNeed/types/missions';
import workerService from '@/modules/worker/services/workerService';
import { MobileAppStatus } from '@/modules/worker/types';

import type {
	AddWorkerTabListSnapshot,
	ApplyBulkAddItemResultCallbacks,
	BuildAddWorkerTabSnapshotInput,
	BuildAddWorkerTabsConfigInput,
	BuildWorkersQueryParamsInput,
	CanFetchWorkersInput,
	FetchWorkersForTabInput,
	FormatWorkerAgenciesLabelInput,
	HandleAddWorkerMissionErrorCallbacks,
	ResolveAddWorkerListStateTextInput,
	WorkerListItemWithAgencies,
} from '@/modules/companyNeed/components/modals/mission/addWorkerToMission/types';
import type { CompanyNeedPaginatedResponse } from '@/modules/companyNeed/types';
import type {
	AddWorkersToMissionBulkStatusItem,
	MissionBulkWorker,
} from '@/modules/companyNeed/types/missions';
import type {
	PoolWorkerAgency,
	PoolWorkersQueryParams,
	WorkerPool,
} from '@/modules/companyNeed/views/details/pool/types';
import type { WorkerListItem } from '@/modules/worker/types';

export const mapWorkerToMissionBulkWorker = (
	worker: WorkerPool
): MissionBulkWorker => ({
	workerId: worker.workerId,
	firstname: worker.firstname,
	lastname: worker.lastname,
	email: worker.email,
	phone: worker.phone,
});

export const mapWorkerListItemToWorkerPool = (
	worker: WorkerListItem
): WorkerPool => {
	const extendedWorker = worker as WorkerListItemWithAgencies;
	const agencyIds =
		extendedWorker.agenciesList ?? extendedWorker.agencies ?? [];

	return {
		workerId: worker.id,
		firstname: worker.firstname ?? '',
		lastname: worker.lastname ?? '',
		avatarUrl: worker.picture ?? null,
		delegable: worker.delegable,
		registered: worker.mobileAppStatus === MobileAppStatus.REGISTERED,
		lastUpdate: worker.updatedAt,
		phone: worker.phone ?? '',
		email: worker.email ?? '',
		missionWithClient: 0,
		distance: 0,
		experienceLevel: null,
		unavailability: null,
		agencies: agencyIds.map(
			(agencyId): PoolWorkerAgency => ({
				agencyId: String(agencyId),
			})
		),
		agencyName: null,
		ongoingProposals: [],
	};
};

export const isServiceUnavailableError = (error: unknown): boolean =>
	(error as { response?: { status?: number } })?.response?.status === 503;

export const isMaxWorkersReachedApiError = (error: unknown): boolean =>
	(error as { response?: { data?: { code?: string } } })?.response?.data
		?.code === MISSION_MAX_WORKERS_REACHED_CODE;

export const canFetchWorkersForTab = ({
	besoinId,
	activeTabId,
	searchLength,
}: CanFetchWorkersInput): boolean => {
	if (!besoinId) {
		return false;
	}

	if (
		activeTabId === AddWorkerTabId.ALL_PROFILES &&
		searchLength < ALL_PROFILES_MIN_SEARCH_LENGTH
	) {
		return false;
	}

	return true;
};

export const buildWorkersQueryParams = ({
	missionId,
	search,
	offset,
	limit,
}: BuildWorkersQueryParamsInput): PoolWorkersQueryParams => ({
	missionId,
	search: search || undefined,
	offset,
	limit,
});

export const fetchWorkersForTab = async ({
	besoinId,
	activeTabId,
	queryParams,
	search,
}: FetchWorkersForTabInput): Promise<
	CompanyNeedPaginatedResponse<WorkerPool>
> => {
	switch (activeTabId) {
		case AddWorkerTabId.POOL:
			return companyNeedService.fetchPaginatedPoolWorkers(
				besoinId,
				queryParams
			);
		case AddWorkerTabId.ONGOING_PROPOSAL:
			return companyNeedService.fetchOngoingProposalWorkers(
				besoinId,
				queryParams
			);
		case AddWorkerTabId.ALL_PROFILES: {
			const limit = queryParams.limit ?? ALL_PROFILES_SEARCH_LIMIT;
			const offset = queryParams.offset ?? 0;
			const page = limit > 0 ? Math.floor(offset / limit) + 1 : 1;
			const response = await workerService.loadWorkers({
				search,
				page,
				limit,
				delegable: true,
			});

			return {
				items: response.items.map(mapWorkerListItemToWorkerPool),
				offset,
				limit,
				page,
				total: response.total,
				totalPages: response.totalPages,
				hasNextPage: page < response.totalPages,
				hasPreviousPage: page > 1,
			};
		}
		default: {
			const unhandledTab: never = activeTabId;
			return Promise.reject(
				new Error(`Unhandled tab: ${String(unhandledTab)}`)
			);
		}
	}
};

export const isCachableAddWorkerTab = (
	tabId: AddWorkerTabId
): tabId is AddWorkerTabId.POOL | AddWorkerTabId.ONGOING_PROPOSAL =>
	tabId === AddWorkerTabId.POOL || tabId === AddWorkerTabId.ONGOING_PROPOSAL;

export const buildAddWorkerTabSnapshot = ({
	items,
	total,
	pageLimit,
	excludedWorkerIds,
}: BuildAddWorkerTabSnapshotInput): AddWorkerTabListSnapshot => {
	const excludedWorkerIdSet = new Set(excludedWorkerIds);

	return {
		items: items.filter((worker) => !excludedWorkerIdSet.has(worker.workerId)),
		total,
		nextOffset: pageLimit,
		hasNextPage: items.length < total,
	};
};

export const resolveAddWorkerListStateText = ({
	isAllProfilesSearchPromptVisible,
	isLoading,
	isServiceUnavailable,
	loadError,
	isListEmpty,
	isFilterEmpty,
	activeTabId,
	t,
}: ResolveAddWorkerListStateTextInput): string | null => {
	if (isAllProfilesSearchPromptVisible) {
		return null;
	}
	if (isLoading) {
		return t('global.loading');
	}
	if (isServiceUnavailable) {
		return t(
			'modals.missionDetails.propositions.addWorkerToMission.serviceUnavailable'
		);
	}
	if (loadError) {
		return t('modals.missionDetails.propositions.addWorkerToMission.loadError');
	}
	if (isFilterEmpty) {
		return t('modals.missionDetails.propositions.addFromPool.noSearchResults');
	}
	if (isListEmpty) {
		if (activeTabId === AddWorkerTabId.ONGOING_PROPOSAL) {
			return t(
				'modals.missionDetails.propositions.addWorkerToMission.emptyOngoingProposal'
			);
		}

		return t('modals.missionDetails.propositions.addFromPool.emptyPool');
	}

	return null;
};

export const buildAddWorkerTabsConfig = ({
	activeTabId,
	t,
	tabCounts,
}: BuildAddWorkerTabsConfigInput) => ({
	tabGroups: [
		{
			tabItems: [
				{
					id: AddWorkerTabId.POOL,
					label: t(
						'modals.missionDetails.propositions.addWorkerToMission.tabs.pool'
					),
					isActive: activeTabId === AddWorkerTabId.POOL,
					icon: 'star',
					count: tabCounts?.[AddWorkerTabId.POOL],
				},
				{
					id: AddWorkerTabId.ONGOING_PROPOSAL,
					label: t(
						'modals.missionDetails.propositions.addWorkerToMission.tabs.ongoingProposal'
					),
					isActive: activeTabId === AddWorkerTabId.ONGOING_PROPOSAL,
					icon: 'order',
					count: tabCounts?.[AddWorkerTabId.ONGOING_PROPOSAL],
				},
			],
			verticalSeparator: true,
		},
		{
			tabItems: [
				{
					id: AddWorkerTabId.ALL_PROFILES,
					label: t(
						'modals.missionDetails.propositions.addWorkerToMission.tabs.allProfiles'
					),
					isActive: activeTabId === AddWorkerTabId.ALL_PROFILES,
					icon: 'users',
				},
			],
		},
	],
	noPadding: true,
	innerPadding: '0 var(--spacing-3xl, 24px)',
});

export const applyBulkAddItemResult = (
	item: AddWorkersToMissionBulkStatusItem | undefined,
	callbacks: ApplyBulkAddItemResultCallbacks
) => {
	if (item?.code === MISSION_MAX_WORKERS_REACHED_CODE) {
		callbacks.onMaxWorkersReached();
		return;
	}

	if (item?.code === MISSION_WORKER_REGISTRATION_NOT_COMPLETE_CODE) {
		callbacks.onRegistrationNotComplete();
		return;
	}

	if (item?.success) {
		callbacks.onSuccess();
		return;
	}

	if (item?.code === MISSION_WORKER_ALREADY_ASSIGNED_CODE) {
		callbacks.onAlreadyAssigned();
		return;
	}

	callbacks.onError();
};

export const handleAddWorkerMissionError = (
	error: unknown,
	callbacks: HandleAddWorkerMissionErrorCallbacks
) => {
	if (isMaxWorkersReachedApiError(error)) {
		callbacks.onMaxWorkersReached();
		return;
	}

	callbacks.onError();
};

export const openTooMuchWorkerConfirmModal = (
	openModal: (modalName: ModalName) => void
) => {
	openModal(ModalName.TooMuchWorkerConfirm);
};

export const formatWorkerAgenciesLabel = ({
	agencies,
	agencyName,
	currentAgencyId,
	t,
}: FormatWorkerAgenciesLabelInput): string | null => {
	if (agencies?.length) {
		const currentId = currentAgencyId ? String(currentAgencyId) : null;
		const isOnCurrentAgency = Boolean(
			currentId &&
				agencies.some((agency) => String(agency.agencyId) === currentId)
		);

		if (isOnCurrentAgency) {
			const otherAgenciesCount = agencies.filter(
				(agency) => String(agency.agencyId) !== currentId
			).length;

			if (otherAgenciesCount === 0) {
				return t('global.myAgency');
			}

			return t(
				'modals.missionDetails.propositions.addWorkerToMission.agencyMyAgencyWithOthers',
				{
					count: otherAgenciesCount,
				}
			);
		}

		const firstAgency = agencies[0];
		const firstName = (
			firstAgency.publicName ??
			firstAgency.agencyName ??
			''
		).trim();

		if (!firstName) {
			return null;
		}

		if (agencies.length > 1) {
			return t('page.worker.profile.header.agencyProfileWithMore', {
				name: firstName,
				count: agencies.length - 1,
			});
		}

		return firstName;
	}

	const normalizedAgencyName = agencyName?.trim();

	return normalizedAgencyName || null;
};
